import type { Deck } from '@point-ker/shared';

import type { JiraSettings } from '@/jira/settings';

/**
 * Builds the `curl` that files a room's estimate against a Jira issue.
 *
 * Nothing here talks to Jira. The command is assembled for the host to read,
 * check and run in a terminal that already has access — this app has no
 * business holding a credential that reaches their whole Jira account, and a
 * request from the browser would be blocked by CORS anyway.
 *
 * Every value that comes from outside is quoted for a POSIX shell, because
 * both the ticket (a story title someone typed) and the credentials arrive as
 * free text and end up on a command line.
 */

/**
 * POSIX single-quoting: everything between the quotes is literal, and an
 * embedded quote is handled by closing, escaping and reopening. Safer than the
 * double quotes of a hand-written command, where `$`, a backtick or a
 * backslash would still be live.
 */
function quote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

/**
 * Accepts either the site name or the whole host, so `acme`,
 * `acme.atlassian.net` and `https://acme.atlassian.net/` all work. A dot is
 * what marks it as already qualified.
 */
export function jiraHost(site: string): string {
  const trimmed = site
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  if (trimmed === '') return '';
  return trimmed.includes('.') ? trimmed : `${trimmed}.atlassian.net`;
}

/**
 * The estimate as JSON, a number where that is honest and a string otherwise.
 *
 * A story points field wants a number, and the label is not always one: the
 * Fibonacci half card reads `½` but is worth 0.5, so the card's own value is
 * preferred over parsing its face. An ordinal deck is deliberately left as a
 * string — `XL` is a rank, and writing 5 into a points field would invent a
 * quantity the room never agreed on.
 */
function estimateJson(estimate: string, deck: Deck): string {
  const card = deck.cards.find((candidate) => candidate.label === estimate);
  if (deck.scale === 'numeric' && card?.value != null) return String(card.value);

  const parsed = Number(estimate);
  if (estimate.trim() !== '' && Number.isFinite(parsed)) return String(parsed);
  return JSON.stringify(estimate);
}

export interface JiraCommandInput {
  readonly settings: JiraSettings;
  readonly token: string;
  /** The story's title, used verbatim as the issue key. */
  readonly ticket: string;
  /** The label the host committed to. */
  readonly estimate: string;
  /** The room's deck, which is what gives a label like `½` its value. */
  readonly deck: Deck;
}

export function buildJiraCurl({
  settings,
  token,
  ticket,
  estimate,
  deck,
}: JiraCommandInput): string {
  const url = `https://${jiraHost(settings.site)}/rest/api/3/issue/${encodeURIComponent(ticket.trim())}`;
  const body = `{"fields":{${JSON.stringify(settings.customField.trim())}:${estimateJson(estimate, deck)}}}`;

  return [
    'curl -s -o /dev/null -w "%{http_code}\\n" \\',
    ` -u ${quote(`${settings.email.trim()}:${token}`)} \\`,
    ` -X PUT ${quote(url)} \\`,
    ' -H "Content-Type: application/json" \\',
    ` -d ${quote(body)}`,
  ].join('\n');
}
